import React, { useCallback, useState } from 'react';
import { TeamReplacementProps } from 'api/teams';
import { confirm } from 'components/atoms/modal';
import { useTranslate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { ReplacementUserForTeam } from '../types';

interface Props {
  roleId?: number;
  projectId: number;
  projectName: string;
  teamId: number;
  teamRoleName?: string;
  selectedReplacementUser?: ReplacementUserForTeam;
  replaceTeamWithUser: (props: TeamReplacementProps) => Promise<any>;
}

interface ReturnProps {
  subModalsVisibile: boolean;
  checkUserRoleAndReplaceTeam: () => void;
}

export const useCheckRoleAndReplaceUser = ({
  roleId,
  projectId,
  projectName,
  teamId,
  teamRoleName,
  selectedReplacementUser,
  replaceTeamWithUser,
}: Props): ReturnProps => {
  const translate = useTranslate();

  const [inviteModalVisibility, setInviteModalVisibility] = useState(false);
  const [roleConfirmationModal, setRoleConfirmationModal] = useState(false);

  const defaultProject = translate(
    'invitation.via.general.confirm.content.default_project',
  );

  const replaceTeam = useCallback(async () => {
    if (selectedReplacementUser != null && roleId != null) {
      await replaceTeamWithUser({
        projectId,
        teamId,
        userId: selectedReplacementUser.userId,
        roleId,
      });
    }
  }, [projectId, replaceTeamWithUser, roleId, selectedReplacementUser, teamId]);

  const inviteUserAndReplaceTeam = useCallback(() => {
    if (selectedReplacementUser != null) {
      if (!selectedReplacementUser.isInProject) {
        setInviteModalVisibility(true);
        confirm({
          title: translate('invitation.team_member.confirm.title'),
          content: translate('invitation.via.general.confirm.content', {
            count: 1,
            emailIds: <b>{selectedReplacementUser.emailId}</b>,
            project: projectName !== '' ? projectName : defaultProject,
          }),
          okText: translate('continue'),
          onOk: () => {
            replaceTeam();
            setInviteModalVisibility(false);
          },
          onCancel: () => {
            setInviteModalVisibility(false);
          },
        });
      } else {
        replaceTeam();
      }
    }
  }, [
    defaultProject,
    projectName,
    replaceTeam,
    selectedReplacementUser,
    translate,
  ]);

  const checkUserRoleAndReplaceTeam = useCallback(() => {
    if (selectedReplacementUser != null) {
      if (selectedReplacementUser.role?.roleId !== roleId) {
        setRoleConfirmationModal(true);
        confirm({
          title: translate(
            'resource_management.find_member.different_user_role_confirmation.title',
          ),
          content: (
            <>
              {translate(
                'resource_management.find_member.different_user_role_confirmation.message1',
                {
                  user: fullName(selectedReplacementUser),
                  userRole: selectedReplacementUser?.role?.roleName,
                  teamRole:
                    teamRoleName ??
                    translate(
                      'resource_management.find_member.different_user_role_confirmation.different_role',
                    ),
                  b: (chunks) => <b>{chunks}</b>,
                },
              )}
              <br />
              <br />
              {translate(
                'resource_management.find_member.different_user_role_confirmation.message2',
              )}
            </>
          ),
          okText: translate(
            'resource_management.find_member.different_user_role_confirmation.confirm_cta',
          ),
          onOk: () => {
            inviteUserAndReplaceTeam();
            setRoleConfirmationModal(false);
          },
          onCancel: () => {
            setRoleConfirmationModal(false);
          },
        });
      } else {
        inviteUserAndReplaceTeam();
      }
    }
  }, [
    inviteUserAndReplaceTeam,
    roleId,
    selectedReplacementUser,
    teamRoleName,
    translate,
  ]);

  return {
    subModalsVisibile: inviteModalVisibility || roleConfirmationModal,
    checkUserRoleAndReplaceTeam,
  };
};
